/**
 * Czy instalacja mieści się w mocy przyłączeniowej, czyli czy wystarczy zgłoszenie mikroinstalacji.
 *
 * Zasady z treści wzoru Enei WZ-MI (21.05.2026) i art. 7 ust. 8d12 Prawa energetycznego:
 * - moc zainstalowana mikroinstalacji PV to moc modułów (stanowisko Prezesa URE: moc znamionowa ogniw);
 * - moc magazynu NIE dolicza się, o ile (1) moc magazynu nie jest większa od progu (do 30.11.2026 moc
 *   mikroinstalacji, od 1.12.2026 jej 2,2-krotność) i (2) łączna moc, jaką instalacja z magazynem może
 *   oddać do sieci, nie jest większa od mocy mikroinstalacji; inaczej moce się sumuje;
 * - zgłoszenie wystarcza, gdy wynik nie przekracza mocy przyłączeniowej; mikroinstalacja to do 50 kW;
 * - przy zasilaniu jednofazowym moc nie może przekraczać 3,68 kW.
 * Moc oddawaną do sieci przyjmujemy jako moc znamionową falownika (hybrydowego).
 *
 * Nowelizacja ustawy o OZE (ustawa z 4.09.2026, Dz.U. 2026 poz. 1347, podpisana 15.10.2026, ogłoszona
 * 16.10.2026) zmienia art. 7 ust. 8d12: próg (1) rośnie do 2,2 × mocy mikroinstalacji, warunek (2) zostaje;
 * w zgłoszeniu podaje się też moc magazynu (art. 7 ust. 8d5 pkt 1). Zmiany obowiązują od 1.12.2026 (art. 41).
 * Art. 31: do zgłoszeń złożonych i nierozpatrzonych przed tym dniem stosuje się nowe przepisy, a operator
 * wzywa do uzupełnienia zgłoszenia o moc magazynu. Dlatego do 30.11.2026 liczymy oba warianty (drugi
 * pokazujemy jako zasady od 1.12.2026), a od 1.12.2026 tylko nowy. Mnożnik wybiera `mnoznikMagazynu(data)`.
 */

export const LIMIT_MIKROINSTALACJI_KW = 50
export const LIMIT_JEDNOFAZOWY_KW = 3.68
export const NOWELIZACJA = {
  mnoznik: 2.2,
  mnoznikPrzed: 1,
  obowiazujeOd: '2026-12-01',
  stan: 'ogłoszona',
  publikacja: 'Dz.U. 2026 poz. 1347',
  sprawdzono: '2026-10-19',
}

/** Dzień kalendarzowy „RRRR-MM-DD” w czasie lokalnym (Date) albo z tekstu daty. */
const dzien = (data) => {
  if (typeof data === 'string') return data.slice(0, 10)
  const d = data instanceof Date ? data : new Date(data)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Czy w danym dniu (domyślnie dziś) obowiązują już zasady nowelizacji (od 1.12.2026). */
export const nowelizacjaObowiazuje = (data = new Date()) => dzien(data) >= NOWELIZACJA.obowiazujeOd

/** Mnożnik progu mocy magazynu w danym dniu: 1 do 30.11.2026, 2,2 od 1.12.2026. */
export const mnoznikMagazynu = (data = new Date()) => (nowelizacjaObowiazuje(data) ? NOWELIZACJA.mnoznik : NOWELIZACJA.mnoznikPrzed)

const liczba = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v))

/**
 * Wejście w kW: moduły (DC), falownik (AC), magazyn (łącznie), moc przyłączeniowa; `zMagazynem`, `jednofazowa`.
 * Wynik: { stan: 'gotowe' | 'brak_danych', brakuje, mikro, magazyn, eksport, liczySieMagazyn, powody,
 * lacznie, tryb: 'zgloszenie' | 'wniosek' | 'poza_mikro' | 'bez_mocy_przylaczeniowej', nadwyzka, jednofazowaPonad,
 * mnoznik, progMagazynu }. Mnożnik domyślnie według dzisiejszej daty.
 */
export const ocenMocy = (wejscie, mnoznik = mnoznikMagazynu()) => {
  const mikro = liczba(wejscie.mocModulowKw)
  const eksport = liczba(wejscie.mocFalownikaKw)
  const magazyn = wejscie.zMagazynem ? liczba(wejscie.mocMagazynuKw) : null
  const przylaczeniowa = liczba(wejscie.mocPrzylaczeniowaKw)
  const brakuje = []
  if (!mikro) brakuje.push('moc modułów')
  if (wejscie.zMagazynem && magazyn == null) brakuje.push('moc magazynu')
  if (wejscie.zMagazynem && eksport == null) brakuje.push('moc falownika')
  if (brakuje.length) return { stan: 'brak_danych', brakuje }

  const progMagazynu = Math.round(mnoznik * mikro * 1000) / 1000
  const powody = []
  if (wejscie.zMagazynem) {
    if (magazyn > progMagazynu) powody.push('magazyn')
    if (eksport > mikro) powody.push('falownik')
  }
  const liczySieMagazyn = powody.length > 0
  const lacznie = Math.round((liczySieMagazyn ? mikro + magazyn : mikro) * 1000) / 1000
  let tryb = 'bez_mocy_przylaczeniowej'
  if (lacznie > LIMIT_MIKROINSTALACJI_KW) tryb = 'poza_mikro'
  else if (przylaczeniowa) tryb = lacznie > przylaczeniowa ? 'wniosek' : 'zgloszenie'
  return {
    stan: 'gotowe', brakuje, mikro, magazyn, eksport, liczySieMagazyn, powody, lacznie, tryb, przylaczeniowa,
    nadwyzka: przylaczeniowa && lacznie > przylaczeniowa ? Math.round((lacznie - przylaczeniowa) * 100) / 100 : 0,
    jednofazowaPonad: Boolean(wejscie.jednofazowa) && lacznie > LIMIT_JEDNOFAZOWY_KW,
    mnoznik, progMagazynu,
  }
}

/**
 * Ocena do narzędzia zgłoszenia. Do 30.11.2026: `obecna` według obecnych przepisów, `poNowelizacji` według
 * zasad od 1.12.2026, gdy daje inny wynik (inaczej null), i `nowelizacjaPrzed` = true. Od 1.12.2026: `obecna`
 * według nowych zasad, `poNowelizacji` null, `nowelizacjaPrzed` = false.
 */
export const ocenaMocyZgloszenia = (wejscie, data = new Date()) => {
  if (nowelizacjaObowiazuje(data)) {
    return { obecna: ocenMocy(wejscie, NOWELIZACJA.mnoznik), poNowelizacji: null, nowelizacjaPrzed: false }
  }
  const obecna = ocenMocy(wejscie, NOWELIZACJA.mnoznikPrzed)
  const nowa = ocenMocy(wejscie, NOWELIZACJA.mnoznik)
  const inna = obecna.stan === 'gotowe' && (nowa.lacznie !== obecna.lacznie || nowa.tryb !== obecna.tryb)
  return { obecna, poNowelizacji: inna ? nowa : null, nowelizacjaPrzed: true }
}
